import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X, Mail, Building2, MapPin, Link2, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { type CrmLead } from '@/lib/supabase'
import { useOutreach, updateLead, PIPELINE_STAGES } from '@/hooks/useCRM'
import { OutreachTimeline } from './OutreachTimeline'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const STAGE_LABELS: Record<string, string> = {
  new: 'New', contacted: 'Contacted', replied: 'Replied', meeting: 'Meeting',
  proposal: 'Proposal', negotiation: 'Negotiation', won: 'Won', lost: 'Lost',
}

type LeadDetailProps = {
  lead: CrmLead | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: () => void
}

const Dialog = DialogPrimitive.Root
const DialogPortal = DialogPrimitive.Portal

export function LeadDetail({ lead, open, onOpenChange, onUpdated }: LeadDetailProps) {
  const { outreach, refetch: refetchOutreach } = useOutreach(lead?.id ?? null)
  const [saving, setSaving] = React.useState(false)

  const handleStageChange = async (stage: CrmLead['pipeline_stage']) => {
    if (!lead) return
    setSaving(true)
    const { error } = await updateLead(lead.id, { pipeline_stage: stage })
    setSaving(false)
    if (!error) onUpdated()
  }

  const handleNotesChange = async (notes: string | null) => {
    if (!lead) return
    setSaving(true)
    const { error } = await updateLead(lead.id, { notes })
    setSaving(false)
    if (!error) onUpdated()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l bg-background shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          )}
          onPointerDownOutside={() => onOpenChange(false)}
          onEscapeKeyDown={() => onOpenChange(false)}
        >
          {lead && (
            <>
              <div className="flex items-center justify-between border-b p-4">
                <DialogPrimitive.Title className="text-lg font-semibold">
                  {lead.full_name || (lead.first_name || lead.last_name ? [lead.first_name, lead.last_name].filter(Boolean).join(' ') : null) || lead.email || 'Lead'}
                </DialogPrimitive.Title>
                <DialogPrimitive.Close asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <X className="h-4 w-4" />
                  </Button>
                </DialogPrimitive.Close>
              </div>
              <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Contact</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {lead.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${lead.email}`} className="text-primary hover:underline">{lead.email}</a>
                      </div>
                    )}
                    {lead.company && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{lead.company}</span>
                      </div>
                    )}
                    {(lead.city || lead.country) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{[lead.city, lead.country].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    {lead.linkedin && (
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <a href={lead.linkedin} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">LinkedIn</a>
                      </div>
                    )}
                    {lead.title && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{lead.title}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Pipeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={lead.pipeline_stage}
                      onChange={(e) => handleStageChange(e.target.value as CrmLead['pipeline_stage'])}
                      disabled={saving}
                    >
                      {PIPELINE_STAGES.map((s) => (
                        <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">Source: {lead.source} · Assigned: {lead.assigned_to ?? '—'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <textarea
                      className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Add notes..."
                      defaultValue={lead.notes ?? ''}
                      onBlur={(e) => handleNotesChange(e.target.value.trim() || null)}
                      disabled={saving}
                    />
                  </CardContent>
                </Card>
                <OutreachTimeline leadId={lead.id} outreach={outreach} onUpdated={refetchOutreach} />
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
